import { Box, Typography } from "@mui/material";
import { DataGrid, GridSortModel } from "@mui/x-data-grid";
import { StratifyDocument, Property } from "@stratify/shared";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { useNavigate } from "react-router-dom";
import {
  selectDocumentsError,
  selectDocumentsLoading,
} from "../store/documentsSelectors";
import { selectPropertyDocuments } from "../../properties/store/propertiesSelectors";
import { usePropertyDocumentListColumns } from "./usePropertyDocumentListColumns";
import { setPropertyDocumentSort } from "../../properties/store/propertiesSlice";

interface PropertyDocumentListProps {
  property: Property;
}

export const PropertyDocumentList: React.FC<PropertyDocumentListProps> = ({
  property,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const documents = useSelector((state: RootState) =>
    selectPropertyDocuments(state, {
      address: property.address,
      strataPlanNumber: property.strataPlanNumber,
    })
  );
  const loading = useSelector(selectDocumentsLoading);
  const error = useSelector(selectDocumentsError);
  const columns = usePropertyDocumentListColumns();

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        dispatch(
          setPropertyDocumentSort({
            sortBy: field,
            sortOrder: sort === "asc" ? "asc" : "desc",
          })
        );
      } else {
        dispatch(
          setPropertyDocumentSort({
            sortBy: "createdAt",
            sortOrder: "desc",
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ height: 500, width: "100%", px: 4 }} data-testid="property-document-list">
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <DataGrid<StratifyDocument>
        loading={loading}
        rows={documents}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        disableRowSelectionOnClick
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        onRowClick={(params) =>
          navigate(`/properties/${property.id}/documents/${params.row.id}`)
        }
        sx={{ cursor: "pointer" }}
      />
    </Box>
  );
};
